import axios from 'axios';
import api from './auth'; // Import the configured axios instance from auth.js
import { fetchLLMConfig } from './config';

const API_BASE_URL = 'http://localhost:8000/api/v1'; // Backend API

// The API_BASE_URL, headers, and timeout are already configured in the imported 'api' instance.
// We will rename the imported 'api' to 'chatApi' for consistency within this file if needed,
// or simply use 'api' directly. For clarity, let's use 'api' directly as it's descriptive.

// No need to create a new chatApi instance here, we'll use the one from auth.js
const chatApi = api; // Use the authenticated api instance

// Optional: Add interceptors if needed for auth or logging, similar to old api.js
// For now, keeping it simple.

/**
 * Sends a query to the AI backend.
 * @param {string} query - The user's text query.
 * @param {string} [provider] - The LLM provider (fetched from backend config if not provided).
 * @param {string} [model] - The LLM model (fetched from backend config if not provided).
 * @returns {Promise<Object>} - The backend's response data.
 */
export const sendQueryToAI = async (query, provider = null, model = null) => {
  // Fetch LLM config if not provided
  const config = await fetchLLMConfig();
  provider = provider || config.provider;
  model = model || config.model;
  if (!query || query.trim() === '') {
    throw new Error('Query cannot be empty');
  }

  try {
    // Check if this is actually a campaign analysis request
    const lowerQuery = query.toLowerCase();
    const isCampaignRequest = 
      (lowerQuery.includes('campaign') || lowerQuery.includes('campagne')) && 
      (lowerQuery.includes('analyse') || lowerQuery.includes('analyze') || 
       lowerQuery.includes('analyser') || lowerQuery.includes('performance') || 
       lowerQuery.includes('perfomance'));
       
    // If this looks like a campaign analysis request, redirect it
    if (isCampaignRequest) {
      console.log(`Detected campaign analysis request in sendQueryToAI: "${query}"`);
      console.log("Redirecting to analyzeCampaignPerformance");
      return analyzeCampaignPerformance(query, provider, model);
    }
    
    // Get Mautic credentials from API (based on current brand)
    let mauticUrl = '';
    let mauticUsername = '';
    let mauticPassword = '';
    
    try {
      const credentials = await fetchUserCredentials();
      mauticUrl = credentials.mautic_url || '';
      mauticUsername = credentials.mautic_username || '';
      mauticPassword = credentials.mautic_password || '';
      console.log(`Fetched credentials from API for current brand`);
    } catch (credError) {
      console.error('Failed to fetch credentials from API:', credError);
    }
    
    // Normal query processing
    console.log(`Sending query to AI: "${query}" using provider: ${provider}, model: ${model}`);
    const response = await chatApi.post('/segments/query', {
      query,
      provider,
      model,
      temperature: 0.0, // As seen in the old api.js
      mautic_url: mauticUrl,
      mautic_username: mauticUsername,
      mautic_password: mauticPassword
    });

    if (!response.data) {
      throw new Error('Empty response received from server');
    }
    
    // We expect response.data to contain the AI's textual response.
    // If the text is nested, e.g., response.data.ai_response_text,
    // we might need to adjust this or the calling component.
    // For now, returning the whole data object.
    return response.data; 
  } catch (error) {
    console.error('Error sending query to AI:', error);
    const errorMessage = 
      error.response?.data?.detail ||
      error.message ||
      'Failed to send query to AI';
    console.error('Error details:', errorMessage);
    throw new Error(`Error processing query: ${errorMessage}`);
  }
};

// We can add other chat-related API functions here later,
// like getChatSessions, createChatSession, loadMessages, etc.

/**
 * Sends a segment analysis query to the AI backend.
 * @param {string} query - The user's text query for analysis.
 * @param {string} [provider] - The LLM provider (fetched from backend config if not provided).
 * @param {string} [model] - The LLM model (fetched from backend config if not provided).
 * @returns {Promise<Object>} - The backend's analysis response data.
 */
export const analyzeSegment = async (query, { provider = null, model = null, signal } = {}) => {
  // Fetch LLM config if not provided
  const config = await fetchLLMConfig();
  provider = provider || config.provider;
  model = model || config.model;
  if (!query || query.trim() === '') {
    throw new Error('Analysis query cannot be empty');
  }

  try {
    // Keep the original query to preserve case sensitivity of segment names
    const originalQuery = query.trim();
    
    console.log(`Sending segment analysis query: "${originalQuery}" using provider: ${provider}, model: ${model}`);
    
    // Get Mautic credentials from API (based on current brand)
    let mauticUrl = '';
    let mauticUsername = '';
    let mauticPassword = '';
    
    try {
      const credentials = await fetchUserCredentials();
      mauticUrl = credentials.mautic_url || '';
      mauticUsername = credentials.mautic_username || '';
      mauticPassword = credentials.mautic_password || '';
      console.log(`Fetched credentials from API for current brand`);
    } catch (credError) {
      console.error('Failed to fetch credentials from API:', credError);
    }
    
    const response = await chatApi.post('/segments/analyze', {
      query: originalQuery,
      provider,
      model,
      temperature: 0.0, // Consistent with other calls
      mautic_url: mauticUrl,
      mautic_username: mauticUsername,
      mautic_password: mauticPassword
    }, { signal });

    if (!response.data) {
      throw new Error('Empty response received from server for segment analysis');
    }
    
    console.log('Segment analysis response:', response.data);
    return response.data; 
  } catch (error) {
    // Si l'erreur est une annulation de requête, on ne la propage pas comme une erreur à afficher.
    if (axios.isCancel(error)) {
      console.log('Segment analysis request canceled:', error.message);
      // On propage l'erreur d'annulation pour que le composant appelant puisse l'ignorer.
      throw error;
    }

    // Pour toutes les autres erreurs, on continue de les logger et de les propager.
    console.error('Error sending segment analysis query to AI:', error);
    const errorMessage = 
      error.response?.data?.detail ||
      error.message ||
      'Failed to send segment analysis query to AI';
    console.error('Error details:', errorMessage);
    throw new Error(`Error processing segment analysis query: ${errorMessage}`);
  }
};

/**
 * Gets precomputed segment analysis from the database (fast endpoint).
 * @param {string} segmentId - The segment ID to get analysis for.
 * @returns {Promise<Object>} - The precomputed analysis response data.
 */
export const getPrecomputedSegmentAnalysis = async (segmentId) => {
  if (!segmentId || segmentId.trim() === '') {
    throw new Error('Segment ID cannot be empty');
  }

  try {
    console.log(`Fetching precomputed analysis for segment: ${segmentId}`);
    const startTime = Date.now();
    
    // Fix URL to match backend endpoint: /segments/{segment_id}/precomputed-analysis
    const encodedSegmentId = encodeURIComponent(segmentId);
    const response = await chatApi.get(`/segments/${encodedSegmentId}/precomputed-analysis`, {
      timeout: 8000 // Increased from 3 seconds to 8 seconds to account for network latency
    });

    const responseTime = Date.now() - startTime;
    console.log(`Precomputed analysis response time: ${responseTime}ms`);

    if (!response.data) {
      throw new Error('Empty response received from server for precomputed analysis');
    }
    
    console.log('Precomputed segment analysis response:', response.data);
    return response.data;
  } catch (error) {
    const responseTime = Date.now() - (error.startTime || Date.now());
    
    // Handle timeout specifically - this is expected behavior, not an error
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.log(`Precomputed analysis timeout for segment ${segmentId} after ${responseTime}ms - falling back to live analysis`);
      throw new Error('TIMEOUT_FALLBACK'); // Special error type for timeout
    }
    
    // Handle 404 errors (no precomputed data available)
    if (error.response && error.response.status === 404) {
      console.log(`No precomputed analysis found for segment ${segmentId} - falling back to live analysis`);
      throw new Error('NO_PRECOMPUTED_DATA'); // Special error type for missing data
    }
    
    // Handle quota errors in precomputed data
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch precomputed segment analysis';
    if (errorMessage.includes("⚠️ Limite de quota API atteinte") || 
        errorMessage.toLowerCase().includes('quota') ||
        errorMessage.toLowerCase().includes('429') ||
        errorMessage.toLowerCase().includes('rate limit')) {
      console.log('Quota error detected in precomputed analysis response');
      throw new Error("⚠️ Limite de quota API atteinte. Votre quota d'utilisation de l'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard.");
    }
    
    // Log actual errors (not timeouts)
    console.error('Error fetching precomputed segment analysis:', error);
    console.error('Error details:', errorMessage);
    console.error('Error status:', error.response?.status);
    console.error('Error response:', error.response?.data);
    throw new Error(`Error fetching precomputed analysis: ${errorMessage}`);
  }
};

/**
 * Sends a campaign performance analysis query to the AI backend.
 * @param {string} query - The user's text query for campaign analysis.
 * @param {string} [provider] - The LLM provider (fetched from backend config if not provided).
 * @param {string} [model] - The LLM model (fetched from backend config if not provided).
 * @returns {Promise<Object>} - The backend's campaign analysis response data.
 */
export const analyzeCampaignPerformance = async (query, { provider = null, model = null, signal } = {}) => {
  // Fetch LLM config if not provided
  const config = await fetchLLMConfig();
  provider = provider || config.provider;
  model = model || config.model;
  if (!query || query.trim() === '') {
    throw new Error('Campaign analysis query cannot be empty');
  }

  try {
    const originalQuery = query.trim();
    console.log(`Sending campaign performance analysis query: "${originalQuery}" using provider: ${provider}, model: ${model}`);

    // Get Mautic credentials from API (based on current brand)
    let mauticUrl = '';
    let mauticUsername = '';
    let mauticPassword = '';
    
    try {
      const credentials = await fetchUserCredentials();
      mauticUrl = credentials.mautic_url || '';
      mauticUsername = credentials.mautic_username || '';
      mauticPassword = credentials.mautic_password || '';
      console.log(`Fetched credentials from API for current brand`);
    } catch (credError) {
      console.error('Failed to fetch credentials from API:', credError);
    }

    const response = await chatApi.post('/campaigns/analyze', {
      query: originalQuery,
      provider,
      model,
      temperature: 0.0,
      mautic_url: mauticUrl,
      mautic_username: mauticUsername,
      mautic_password: mauticPassword
    }, { signal });

    if (!response.data) {
      throw new Error('Empty response received from server for campaign performance analysis');
    }
    
    console.log('Campaign analysis response received:', response.data);
    
    if (response.data.error) {
      console.warn('Campaign analysis error:', response.data.error);
      return {
        success: false,
        message: `Could not analyze campaign: ${response.data.error}`,
        response: `I couldn't analyze the campaign performance. ${response.data.error}`
      };
    }
    
    return response.data; 
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Campaign analysis request canceled:', error.message);
      return { success: false, message: 'Request canceled by user.', error: 'canceled' };
    }

    console.error('Error sending campaign performance analysis query to AI:', error);
    const errorMessage = 
      error.response?.data?.detail ||
      error.message ||
      'Failed to send campaign performance analysis query to AI';
    console.error('Error details:', errorMessage);
    
    return {
      success: false,
      message: `Error analyzing campaign performance: ${errorMessage}`,
      response: "I couldn't analyze the campaign performance. This could be because the campaign doesn't exist or there's an issue with the data. Please check the campaign name and try again."
    };
  }
};

// --- Chat Session and Message Management Functions ---

/**
 * Get all chats for the current user.
 * @returns {Promise<Array>} - A list of the user's chat sessions.
 */
export const getUserChats = async () => {
  try {
    const response = await chatApi.get('/chats/');
    return response.data;
  } catch (error) {
    console.error('Error fetching user chats:', error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch chats');
  }
};

/**
 * Create a new chat session.
 * @param {string} title - The initial title for the chat.
 * @returns {Promise<Object>} - The newly created chat object.
 */
export const createChat = async (title) => {
  try {
    const response = await chatApi.post('/chats/', { title });
    return response.data;
  } catch (error) {
    console.error('Error creating chat:', error);
    throw new Error(error.response?.data?.detail || 'Failed to create chat');
  }
};

/**
 * Get all messages for a specific chat session.
 * @param {string} chatId - The ID of the chat.
 * @returns {Promise<Array>} - A list of messages for the specified chat.
 */
export const getChatMessages = async (chatId) => {
  try {
    const response = await chatApi.get(`/chats/${chatId}/messages`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching messages for chat ${chatId}:`, error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch messages');
  }
};

/**
 * Create a new message in a chat.
 * @param {string} chatId - The ID of the chat.
 * @param {string} role - The role of the message sender ('user' or 'bot').
 * @param {string} content - The message content.
 * @returns {Promise<Object>} - The newly created message object.
 */
export const createMessage = async (chatId, role, content) => {
  try {
    const response = await chatApi.post(`/chats/${chatId}/messages`, { role, content });
    return response.data;
  } catch (error) {
    console.error(`Error creating message in chat ${chatId}:`, error);
    // Don't wrap the error - throw it as-is so we can access error.response.data properly
    throw error;
  }
};

/**
 * Update a chat's title.
 * @param {string} chatId - The ID of the chat.
 * @param {string} title - The new title for the chat.
 * @returns {Promise<Object>} - The updated chat object.
 */
export const updateChat = async (chatId, title) => {
  try {
    const response = await chatApi.put(`/chats/${chatId}`, { title });
    return response.data;
  } catch (error) {
    console.error(`Error updating chat ${chatId}:`, error);
    throw new Error(error.response?.data?.detail || 'Failed to update chat');
  }
};

/**
 * Delete a chat session.
 * @param {string} chatId - The ID of the chat to delete.
 * @returns {Promise<Object>} - The deleted chat object.
 */
export const deleteChat = async (chatId) => {
  try {
    const response = await chatApi.delete(`/chats/${chatId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting chat ${chatId}:`, error);
    throw new Error(error.response?.data?.detail || 'Failed to delete chat');
  }
};

/**
 * Detect the intent of a user message.
 * @param {string} query - The user's message.
 * @param {string} [provider] - The LLM provider (fetched from backend config if not provided).
 * @param {string} [model] - The LLM model (fetched from backend config if not provided).
 * @returns {Promise<Object>} - The detected intent information.
 */
export const detectIntent = async (query, provider = null, model = null) => {
  // Fetch LLM config if not provided
  const config = await fetchLLMConfig();
  provider = provider || config.provider;
  model = model || config.model;
  try {
    const response = await chatApi.post('/intent/detect', { query, provider, model });
    return response.data;
  } catch (error) {
    console.error('Error detecting intent:', error);
    throw new Error(error.response?.data?.detail || 'Failed to detect intent');
  }
};

// --- Knowledge Base API Functions ---

/**
 * Upload a document to the knowledge base.
 * @param {File} file - The file to upload.
 * @param {Object} [metadata=null] - Optional metadata for the document.
 * @returns {Promise<Object>} - The uploaded document information.
 */
export const uploadDocument = async (file, metadata = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    if (metadata) {
      formData.append('metadata', JSON.stringify(metadata));
    }
    
    const response = await chatApi.post('/kb/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw new Error(error.response?.data?.detail || 'Failed to upload document');
  }
};

/**
 * List all documents in the knowledge base.
 * @returns {Promise<Array>} - A list of documents.
 */
export const listDocuments = async () => {
  try {
    const response = await chatApi.get('/kb/documents');
    return response.data.documents;
  } catch (error) {
    console.error('Error listing documents:', error);
    throw new Error(error.response?.data?.detail || 'Failed to list documents');
  }
};

/**
 * Get all chunks for a specific document.
 * @param {string} documentId - The ID of the document.
 * @returns {Promise<Array>} - A list of document chunks.
 */
export const getDocumentChunks = async (documentId) => {
  try {
    const response = await chatApi.get(`/kb/documents/${documentId}`);
    return response.data.chunks;
  } catch (error) {
    console.error(`Error fetching chunks for document ${documentId}:`, error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch document chunks');
  }
};

/**
 * Delete a document from the knowledge base.
 * @param {string} documentId - The ID of the document to delete.
 * @returns {Promise<Object>} - The result of the deletion.
 */
export const deleteDocument = async (documentId) => {
  try {
    const response = await chatApi.delete(`/kb/documents/${documentId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting document ${documentId}:`, error);
    throw new Error(error.response?.data?.detail || 'Failed to delete document');
  }
};

/**
 * Query the knowledge base with a question.
 * @param {string} query - The question to ask.
 * @param {string} [provider] - The LLM provider (fetched from backend config if not provided).
 * @param {string} [model] - The LLM model (fetched from backend config if not provided).
 * @param {number} [temperature] - The temperature for LLM generation (fetched from backend config if not provided).
 * @returns {Promise<Object>} - The answer and related information.
 */
export const queryKnowledgeBase = async (query, provider = null, model = null, temperature = null) => {
  // Fetch LLM config if not provided
  const config = await fetchLLMConfig();
  provider = provider || config.provider;
  model = model || config.model;
  temperature = temperature !== null ? temperature : config.temperature;
  try {
    const response = await chatApi.post('/kb/query', {
      query,
      provider,
      model,
      temperature
    });
    
    return response.data;
  } catch (error) {
    console.error('Error querying knowledge base:', error);
    throw new Error(error.response?.data?.detail || 'Failed to query knowledge base');
  }
};

/**
 * Crée une variante A/B test via prompt LLM côté backend.
 * @param {string} prompt - Le prompt utilisateur décrivant l'A/B test à créer.
 * @returns {Promise<Object>} - La réponse du backend (succès ou erreur).
 */
export const createAbTestViaLLM = async (prompt) => {
  try {
    const response = await chatApi.post('/emails/abtest/llm', { prompt });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la création de l\'A/B test via LLM:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Erreur inconnue lors de la création de l\'A/B test',
      data: null
    };
  }
};

/**
 * Génère une campagne à partir d'un prompt en langage naturel.
 * @param {string} prompt - Le prompt utilisateur décrivant la campagne.
 * @param {Object} mauticCredentials - Mautic credentials for the campaign.
 * @returns {Promise<Object>} - La réponse du backend (succès, JSON de campagne, etc).
 */
export const generateCampaignFromPrompt = async (prompt, mauticCredentials = {}) => {
  try {
    const response = await chatApi.post('/campaigns/generate-from-prompt', { prompt, ...mauticCredentials });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la génération de la campagne depuis le prompt:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Erreur inconnue lors de la génération de la campagne',
      data: null
    };
  }
};

/**
 * Sends a campaign comparison query to the AI backend.
 * @param {string} query - The user's text query requesting a campaign comparison.
 * @param {string} [provider] - The LLM provider (fetched from backend config if not provided).
 * @param {string} [model] - The LLM model (fetched from backend config if not provided).
 * @returns {Promise<Object>} - The backend's comparison response data.
 */
export const compareCampaigns = async (query, provider = null, model = null) => {
  // Fetch LLM config if not provided
  const config = await fetchLLMConfig();
  provider = provider || config.provider;
  model = model || config.model;
  if (!query || query.trim() === '') {
    throw new Error('Query cannot be empty');
  }

  try {
    console.log(`Sending campaign comparison query: "${query}" using provider: ${provider}, model: ${model}`);
    
    // Get Mautic credentials from API (based on current brand)
    let mauticUrl = '';
    let mauticUsername = '';
    let mauticPassword = '';
    
    try {
      const credentials = await fetchUserCredentials();
      mauticUrl = credentials.mautic_url || '';
      mauticUsername = credentials.mautic_username || '';
      mauticPassword = credentials.mautic_password || '';
      console.log(`Fetched credentials from API for current brand`);
    } catch (credError) {
      console.error('Failed to fetch credentials from API:', credError);
    }
    
    const response = await chatApi.post('/campaigns/compare', {
      query,
      provider,
      model,
      mautic_url: mauticUrl,
      mautic_username: mauticUsername,
      mautic_password: mauticPassword
    });

    if (!response.data) {
      throw new Error('Empty response received from server');
    }
    
    console.log('Campaign comparison response received:', response.data);
    
    // Check if the response contains a proper comparison or an error message
    if (response.data.error) {
      console.warn('Campaign comparison error:', response.data.error);
      return {
        success: false,
        message: `Could not compare campaigns: ${response.data.error}`,
        response: `I couldn't compare the campaigns. ${response.data.error}`
      };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error sending campaign comparison query to AI:', error);
    const errorMessage = 
      error.response?.data?.detail ||
      error.message ||
      'Failed to send campaign comparison query';
    console.error('Error details:', errorMessage);
    
    // Provide a more helpful error message
    return {
      success: false,
      message: `Error comparing campaigns: ${errorMessage}`,
      response: "I couldn't compare the campaigns. This could be because one or both campaigns don't exist or there's an issue with the data. Please check the campaign names and try again."
    };
  }
};

/**
 * Crée une campagne dans Mautic à partir d'un JSON de campagne.
 * @param {Object} campaignJson - Le JSON de la campagne à créer.
 * @param {Object} mauticCredentials - Mautic credentials for the campaign.
 * @returns {Promise<Object>} - La réponse du backend (succès ou erreur).
 */
export const createCampaignFromJson = async (campaignJson, mauticCredentials = {}) => {
  try {
    const response = await chatApi.post('/campaigns/create-from-json', { campaign_json: campaignJson, ...mauticCredentials });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la création de la campagne dans Mautic:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Erreur inconnue lors de la création de la campagne',
      data: null
    };
  }
};

/**
 * Suggest segments based on natural language input
 * @param {string} query - The natural language query
 * @param {string} [provider] - The LLM provider (fetched from backend config if not provided)
 * @param {string} [model] - The LLM model (fetched from backend config if not provided)
 * @returns {Promise<Object>} - The suggestions response
 */
export const suggestSegments = async (query, provider = null, model = null) => {
  // Fetch LLM config if not provided
  const config = await fetchLLMConfig();
  provider = provider || config.provider;
  model = model || config.model;
  try {
    console.log(`Suggesting segments with: "${query}" using ${provider} / ${model}`);
    
    // Ensure query is not empty
    if (!query || query.trim() === '') {
      throw new Error('Query cannot be empty');
    }
    
    // Get user's brand from localStorage
    const authData = JSON.parse(localStorage.getItem('user')) || {};
    const userBrand = authData.brand || 'batira'; // Use 'batira' as fallback if no brand is found
    console.log(`Using brand '${userBrand}' for segment suggestions`);
    
    // Get Mautic credentials - first try API, then fallback to localStorage if needed
    let mauticUrl = '';
    let mauticUsername = '';
    let mauticPassword = '';
    let hasMauticCredentials = false;
    
    try {
      // Get fresh credentials from API
      const credentials = await fetchUserCredentials();
      mauticUrl = credentials.mautic_url || '';
      mauticUsername = credentials.mautic_username || '';
      mauticPassword = credentials.mautic_password || '';
      hasMauticCredentials = !!(mauticUrl && mauticUsername && mauticPassword);
      console.log(`Fetched Mautic credentials from API: ${hasMauticCredentials ? 'Available' : 'Not available'}`);
    } catch (credError) {
      console.error('Failed to fetch credentials from API, using localStorage as fallback:', credError);
      // Fallback to localStorage
      mauticUrl = authData.mautic_url || '';
      mauticUsername = authData.mautic_username || '';
      mauticPassword = authData.mautic_password || '';
      hasMauticCredentials = !!(mauticUrl && mauticUsername && mauticPassword);
    }
    
    // Create request payload
    const payload = {
      query,
      provider,
      model,
      temperature: 0.0,
      brand: userBrand // Pass the user's brand
    };
    
    // Add Mautic credentials when available
    if (hasMauticCredentials) {
      payload.mautic_url = mauticUrl;
      payload.mautic_username = mauticUsername;
      payload.mautic_password = mauticPassword;
    }
    
    const response = await chatApi.post('/segments/suggest', payload);
    
    // Validate response
    if (!response.data) {
      throw new Error('Empty response received from server');
    }
    
    // Log result
    console.log(`Segment suggestions received: ${response.data.success ? 'Success' : 'Failed'}`);
    if (response.data.suggestions) {
      console.log(`Number of suggestions: ${response.data.suggestions.length}`);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error suggesting segments:', error);
    
    // Provide more detailed error message
    const errorMessage = 
      error.response?.data?.detail ||
      error.message ||
      'Failed to suggest segments';
    
    console.error('Error details:', errorMessage);
    
    // If the error message is already properly formatted (starts with emoji or mentions quota/API), 
    // don't add the "Error suggesting segments:" prefix
    if (errorMessage.match(/^[⚠️❌✅]/) || 
        errorMessage.toLowerCase().includes('quota') ||
        errorMessage.toLowerCase().includes('limite') ||
        errorMessage.toLowerCase().includes('api') ||
        errorMessage.toLowerCase().includes('token')) {
      throw new Error(errorMessage);
    } else {
      throw new Error(`Error suggesting segments: ${errorMessage}`);
    }
  }
};

/**
 * Create a segment in either Unomi or Mautic
 * @param {string} segmentType - The type of segment to create ('unomi' or 'mautic')
 * @param {Object} segmentConfig - The segment configuration
 * @returns {Promise<Object>} - The creation response
 */
export const createSegmentAtomic = async (unomiConfig, mauticConfig = null, mauticCredentials = null) => {
  try {
    console.log('Creating segments atomically...');
    console.log('Unomi config:', unomiConfig);
    console.log('Mautic config provided:', !!mauticConfig);
    console.log('Mautic credentials provided:', !!mauticCredentials);

    const response = await chatApi.post('/segments/create-atomic', {
      unomi_config: unomiConfig,
      mautic_config: mauticConfig,
      mautic_credentials: mauticCredentials
    });

    console.log('Atomic segment creation response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating segments atomically:', error);
    
    // Extract error message
    let errorMessage = 'Une erreur s\'est produite lors de la création du segment.';
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Return structured error response
    return {
      success: false,
      message: errorMessage,
      unomi_segment: null,
      mautic_segment: null,
      error_type: 'creation_error'
    };
  }
};

export const createSegment = async (segmentType, segmentConfig) => {
  try {
    console.log(`Creating ${segmentType} segment`);
    
    // Ensure segment type is valid
    if (!segmentType || (segmentType !== 'unomi' && segmentType !== 'mautic')) {
      throw new Error('Invalid segment type. Must be "unomi" or "mautic"');
    }
    
    // Ensure segment config is not empty
    if (!segmentConfig) {
      throw new Error('Segment configuration cannot be empty');
    }
    
    // Get user's brand from localStorage or from the passed config
    const authData = JSON.parse(localStorage.getItem('user')) || {};
    const userBrand = segmentConfig.brand || authData.brand || 'batira';
    
    // Get Mautic credentials from config or try to fetch from API for Mautic segments
    let mauticUrl = segmentConfig.mautic_url || '';
    let mauticUsername = segmentConfig.mautic_username || '';
    let mauticPassword = segmentConfig.mautic_password || '';
    
    // For Mautic segments, if credentials aren't in the config, try to get fresh ones
    if (segmentType === 'mautic' && (!mauticUrl || !mauticUsername || !mauticPassword)) {
      try {
        console.log('Mautic credentials not in config, fetching from API');
        const credentials = await fetchUserCredentials();
        mauticUrl = credentials.mautic_url || mauticUrl;
        mauticUsername = credentials.mautic_username || mauticUsername;
        mauticPassword = credentials.mautic_password || mauticPassword;
      } catch (credError) {
        console.error('Failed to fetch Mautic credentials:', credError);
      }
    }
    
    // Debug log to check credentials
    if (segmentType === 'mautic') {
      console.log(`Mautic credentials available: URL=${!!mauticUrl}, Username=${!!mauticUsername}, Password=${!!mauticPassword}`);
    }
    
    // Prepare request data
    const requestData = {
      segment_type: segmentType,
      segment_config: segmentConfig,
      brand: userBrand
    };
    
    // Add Mautic credentials specifically
    if (segmentType === 'mautic') {
      requestData.segment_config.mautic_url = mauticUrl;
      requestData.segment_config.mautic_username = mauticUsername;
      requestData.segment_config.mautic_password = mauticPassword;
    }
    
    console.log(`Sending segment creation request for ${segmentType} segment`);
    const response = await chatApi.post('/segments/create', requestData);
    
    // Validate response
    if (!response.data) {
      throw new Error('Empty response received from server');
    }
    
    return response.data;
  } catch (error) {
    console.error(`Error creating ${segmentType} segment:`, error);
    
    // Check for specific Mautic credential errors
    if (segmentType === 'mautic' && (error.message?.includes('credentials') || error.response?.data?.detail?.includes('credentials'))) {
      return {
        success: false,
        message: "Mautic credentials missing. Please update your Mautic settings in your profile.",
        segment: null
      };
    }
    
    // Get the error message from the response or the error object
    const errorMessage = 
      error.response?.data?.detail ||
      error.message ||
      `Failed to create ${segmentType} segment`;
    
    console.error('Error details:', errorMessage);
    
    // Check for server accessibility errors
    const errorLower = errorMessage.toLowerCase();
    if (errorLower.includes('accessible') || errorLower.includes('serveur') || 
        errorLower.includes('connectivité') || errorLower.includes('connexion')) {
      return {
        success: false,
        message: errorMessage, // Use the backend's generic message
        segment: null,
        error_type: 'server_inaccessible'
      };
    }
    
    // Check for timeout errors and provide user-friendly messages
    if (errorLower.includes('timeout') || errorLower.includes('timed out') || 
        errorLower.includes('read timeout') || errorLower.includes('connectionpool')) {
      return {
        success: false,
        message: 'La création du segment a pris trop de temps. Le segment pourrait avoir été créé. Veuillez vérifier et réessayer si nécessaire.',
        segment: null,
        error_type: 'timeout'
      };
    }
    
    // Check for connection errors
    if (errorLower.includes('connection') || errorLower.includes('network') || 
        errorLower.includes('connect')) {
      return {
        success: false,
        message: 'Problème de connexion détecté. Veuillez vérifier votre connexion et réessayer.',
        segment: null,
        error_type: 'connection_error'
      };
    }
    
    // For other errors, check if we already have a user-friendly message from backend
    if (errorMessage.includes('quota') || errorMessage.includes('limite') ||
        errorMessage.match(/^[⚠️❌✅]/)) {
      // Already user-friendly, pass it through
      return {
        success: false,
        message: errorMessage,
        segment: null
      };
    }
    
    // For unknown errors, provide a generic user-friendly message
    return {
      success: false,
      message: 'Une erreur s\'est produite lors de la création du segment. Veuillez réessayer.',
      segment: null,
      error_type: 'creation_error'
    };
  }
};

/**
 * Fetch user credentials including Mautic credentials from the API
 * @returns {Promise<Object>} - The user credentials
 */
export const fetchUserCredentials = async () => {
  try {
    const response = await chatApi.get('/auth/me/credentials');
    if (response.data && response.data.success && response.data.credentials) {
      return response.data.credentials;
    }
    throw new Error('Failed to fetch user credentials');
  } catch (error) {
    console.error('Error fetching user credentials:', error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch user credentials');
  }
};

/**
 * Create segment directly based on user's natural language description
 * @param {string} query - The natural language description of the segment to create
 * @param {string} [provider] - The LLM provider (fetched from backend config if not provided)
 * @param {string} [model] - The LLM model (fetched from backend config if not provided)
 * @returns {Promise<Object>} - The creation response
 */
export const createSegmentDirect = async (query, provider = null, model = null) => {
  // Fetch LLM config if not provided
  const config = await fetchLLMConfig();
  provider = provider || config.provider;
  model = model || config.model;
  try {
    console.log(`Creating segment directly from query: "${query}" using ${provider} / ${model}`);
    
    // Ensure query is not empty
    if (!query || query.trim() === '') {
      throw new Error('Query cannot be empty');
    }
    
    // Get user's brand from localStorage for the UI
    const authData = JSON.parse(localStorage.getItem('user')) || {};
    const userBrand = authData.brand || 'batira'; // Use 'batira' as fallback if no brand is found
    console.log(`Using brand '${userBrand}' for segment creation UI`);
    
    // Fetch fresh credentials from API instead of localStorage
    let mauticUrl = '';
    let mauticUsername = '';
    let mauticPassword = '';
    let hasMauticCredentials = false;
    
    try {
      const credentials = await fetchUserCredentials();
      mauticUrl = credentials.mautic_url || '';
      mauticUsername = credentials.mautic_username || '';
      mauticPassword = credentials.mautic_password || '';
      hasMauticCredentials = !!(mauticUrl && mauticUsername && mauticPassword);
      console.log(`Fetched credentials from API. Mautic credentials available: ${hasMauticCredentials ? 'Yes' : 'No'}`);
    } catch (credError) {
      console.error('Failed to fetch credentials from API:', credError);
      console.log('Will attempt to create only in Unomi without Mautic credentials');
    }
    
    // Step 1: Get segment suggestions
    const suggestions = await suggestSegments(query, provider, model);
    
    if (!suggestions.success || !suggestions.suggestions || suggestions.suggestions.length === 0) {
      // Check if this is a quota error or other API error that should be passed through
      const errorMessage = suggestions.message || 'Failed to generate segment suggestions';
      
      // If it's already a properly formatted error (quota, API limit, etc.), pass it through directly
      if (errorMessage.match(/^[⚠️❌✅]/) || 
          errorMessage.toLowerCase().includes('quota') ||
          errorMessage.toLowerCase().includes('limite') ||
          errorMessage.toLowerCase().includes('api') ||
          errorMessage.toLowerCase().includes('token') ||
          errorMessage.toLowerCase().includes('billing') ||
          errorMessage.toLowerCase().includes('exceeded') ||
          errorMessage.toLowerCase().includes('serveur') ||
          errorMessage.toLowerCase().includes('accessible')) {
        throw new Error(errorMessage);
      } else {
        throw new Error(`Une erreur s'est produite lors de l'analyse de votre demande: ${errorMessage}`);
      }
    }
    
    // Step 2: Use the first suggestion to create segments atomically in both Unomi and Mautic
    const segment = suggestions.suggestions[0];
    
    // Backend normalizes brand scope and AND/OR structure in _ensure_unomi_config_compliance
    const modifiedUnomiConfig = JSON.parse(JSON.stringify(segment.unomi_config));
    
    // Make sure the scope is set to the user's brand
    if (modifiedUnomiConfig.metadata) {
      modifiedUnomiConfig.metadata.scope = userBrand;
    }
    
    // Prepare Mautic config and credentials if available
    let mauticConfig = null;
    let mauticCredentials = null;
    
    if (hasMauticCredentials && segment.mautic_config) {
      // Prepare Mautic config
      mauticConfig = {...segment.mautic_config};
      
        // Get segment ID from Unomi config if available
        const segmentId = segment.unomi_config?.metadata?.id;
        if (!mauticConfig.tag_name && segmentId) {
          // Use segment ID for tag name if available
          mauticConfig.tag_name = segmentId;
        } else if (!mauticConfig.tag_name) {
          // Fallback to name if ID is not available
          mauticConfig.tag_name = mauticConfig.name;
        }
        
      // Prepare Mautic credentials
      mauticCredentials = {
        mautic_url: mauticUrl,
        mautic_username: mauticUsername,
        mautic_password: mauticPassword
      };
    }
    
    // Create segments atomically - this ensures both are created or neither is created
    const creationResult = await createSegmentAtomic(modifiedUnomiConfig, mauticConfig, mauticCredentials);
    
    // Step 3: Handle atomic creation result
    
    // Check for duplicate detection
    if (creationResult.segmentExists) {
      return {
        success: false,
        message: creationResult.message || `⚠️ Le segment "${segment.name}" existe déjà. Veuillez choisir un autre nom ou modifier le segment existant.`,
        segment_name: creationResult.segmentName || segment.name,
        segment_description: segment.description,
        isDuplicate: true,
        atomicResult: creationResult
      };
    }

    // Check for success
    if (creationResult.success) {
      let successMessage = `✅ Segment "${segment.name}" créé avec succès!`;
      if (creationResult.mautic_segment) {
        successMessage += '';
      } else {
        successMessage += '';
      }
      successMessage += ` Cliquez [ici](#segment-${encodeURIComponent(segment.name)}) pour le consulter.`;
      
        return {
        success: true,
        message: successMessage,
          segment_name: segment.name,
          segment_description: segment.description,
        atomicResult: creationResult
      };
    } else {
      // Handle atomic creation failure
      let errorMessage = creationResult.message || 'Une erreur s\'est produite lors de la création du segment.';
      
      // Add rollback information if available
      if (creationResult.rollback_performed) {
        errorMessage += ' L\'opération a été annulée pour maintenir la cohérence entre les systèmes.';
    }
 
    return {
        success: false,
        message: errorMessage,
        segment_name: segment.name,
        segment_description: segment.description,
        atomicResult: creationResult
      };
    }
  } catch (error) {
    console.error('Error creating segment directly:', error);
    
    // Check if this is already a properly formatted error message (e.g. quota error)
    const errorMessage = error.message || 'Unknown error';
    let finalMessage;
    
    // If the error message is already in the proper format (starts with ⚠️, ❌, etc.) 
    // or mentions quota/API limits, don't wrap it further
    if (errorMessage.match(/^[⚠️❌✅]/) || 
        errorMessage.toLowerCase().includes('quota') ||
        errorMessage.toLowerCase().includes('limite') ||
        errorMessage.toLowerCase().includes('api') ||
        errorMessage.toLowerCase().includes('token') ||
        errorMessage.toLowerCase().includes('billing') ||
        errorMessage.toLowerCase().includes('exceeded') ||
        errorMessage.toLowerCase().includes('rate limit')) {
      finalMessage = errorMessage;
    } else {
      finalMessage = `Error creating segment: ${errorMessage}`;
    }
    
    return {
      success: false,
      message: finalMessage,
      unomiResult: null,
      mauticResult: null
    };
  }
};

/**
 * Crée une campagne de test sur un sous-segment aléatoire d'un segment existant.
 * @param {string} prompt - Le prompt utilisateur
 * @param {number} percent - Le pourcentage de contacts à sélectionner
 * @param {string} email_name - Le nom de l'email à utiliser
 * @param {string} segment_name - Le nom du segment source
 * @returns {Promise<Object>} - La réponse du backend
 */
export const createTestCampaign = async (prompt, percent, email_name, segment_name) => {
  try {
    const response = await chatApi.post('/campaigns/create-test-campaign', {
      prompt,
      percent,
      email_name,
      segment_name
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la création de la campagne de test:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Erreur inconnue lors de la création de la campagne de test',
      data: null
    };
  }
};

/**
 * Récupère les statistiques globales d'un email spécifique.
 * @param {string} emailId - L'ID de l'email
 * @returns {Promise<Object>} - Les statistiques globales de l'email
 */
export const getEmailGlobalStats = async (emailId) => {
  if (!emailId || emailId.trim() === '') {
    throw new Error('Email ID cannot be empty');
  }

  try {
    console.log(`Fetching global stats for email: ${emailId}`);
    
    const response = await chatApi.get(`/emails/${emailId}/global-stats`);

    if (!response.data) {
      throw new Error('Empty response received from server for email stats');
    }
    
    console.log('Email global stats response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching email global stats:', error);
    const errorMessage = 
      error.response?.data?.detail ||
      error.message ||
      'Failed to fetch email global stats';
    console.error('Error details:', errorMessage);
    throw new Error(`Error fetching email stats: ${errorMessage}`);
  }
};

/**
 * Analyse IA d'un email spécifique.
 * @param {string} emailId - L'ID de l'email
 * @param {string} emailName - Le nom de l'email
 * @param {Object} options - Options d'analyse (provider, model, signal, etc.)
 * @returns {Promise<Object>} - L'analyse IA de l'email
 */
export const analyzeEmailPerformance = async (emailId, emailName, options = {}) => {
  if (!emailId || emailId.trim() === '') {
    throw new Error('Email ID cannot be empty');
  }

  try {
    const {
      provider = 'google',
      model = 'gemini-1.5-flash',
      temperature = 0.0,
      query = null,
      signal = null
    } = options;

    // Construire la requête d'analyse
    const analysisQuery = query || `Fournis une analyse détaillée de l'email "${emailName || `Email ${emailId}`}". Analyse les performances, identifie les points forts et faibles, et propose des améliorations concrètes.`;
    
    console.log(`Analyzing email performance: ${emailId} (${emailName}) using ${provider}/${model}`);
    
    // Get Mautic credentials from API (based on current brand)
    let mauticUrl = '';
    let mauticUsername = '';
    let mauticPassword = '';
    
    try {
      const credentials = await fetchUserCredentials();
      mauticUrl = credentials.mautic_url || '';
      mauticUsername = credentials.mautic_username || '';
      mauticPassword = credentials.mautic_password || '';
      console.log(`Fetched credentials from API for current brand`);
    } catch (credError) {
      console.error('Failed to fetch credentials from API:', credError);
    }
    
    const response = await chatApi.post('/emails/analyze', {
      email_id: emailId,
      email_name: emailName,
      query: analysisQuery,
      provider,
      model,
      temperature,
      mautic_url: mauticUrl,
      mautic_username: mauticUsername,
      mautic_password: mauticPassword
    }, { signal });

    if (!response.data) {
      throw new Error('Empty response received from server for email analysis');
    }
    
    console.log('Email analysis response:', response.data);
    return response.data;
  } catch (error) {
    // Si l'erreur est une annulation de requête, on ne la propage pas comme une erreur à afficher.
    if (axios.isCancel(error)) {
      console.log('Email analysis request canceled:', error.message);
      // On propage l'erreur d'annulation pour que le composant appelant puisse l'ignorer.
      throw error;
    }

    // Pour toutes les autres erreurs, on continue de les logger et de les propager.
    console.error('Error analyzing email performance:', error);
    const errorMessage = 
      error.response?.data?.detail ||
      error.message ||
      'Failed to analyze email performance';
    console.error('Error details:', errorMessage);
    throw new Error(`Error analyzing email: ${errorMessage}`);
  }
};

/**
 * Toggles a campaign's publish status (activate/deactivate)
 * @param {string} campaignName - The name of the campaign to toggle
 * @param {boolean} isPublished - The new publish status (true to activate, false to deactivate)
 * @returns {Promise<Object>} - The response from the server
 */
export const toggleCampaignPublishStatus = async (campaignName, isPublished) => {
  try {
    // First, find the campaign by name using search to query all campaigns
    console.log(`Searching for campaign with name: "${campaignName}"`);
    
    // Use search parameter to filter campaigns by name (server-side search)
    // This is much more efficient than fetching all campaigns
    const searchTerm = encodeURIComponent(campaignName);
    const campaignsResponse = await api.get(`/campaigns?search=${searchTerm}&limit=50`);
    const campaigns = campaignsResponse.data.campaigns || [];
    
    // Log all campaign names for debugging
    console.log('Search results:', campaigns.map(c => c.name));
    
    // Find the campaign with the matching name
    // Try exact match first (case-sensitive)
    let campaign = campaigns.find(c => c.name === campaignName);
    
    // If no exact match, try case-insensitive
    if (!campaign) {
      campaign = campaigns.find(c => 
        c.name.toLowerCase() === campaignName.toLowerCase()
      );
    }
    
    // If still not found, try partial match (campaign name contains search term or vice versa)
    if (!campaign) {
      campaign = campaigns.find(c => 
        c.name.toLowerCase().includes(campaignName.toLowerCase()) ||
        campaignName.toLowerCase().includes(c.name.toLowerCase())
      );
    }
    
    if (!campaign) {
      console.error(`Campaign with name "${campaignName}" not found in search results.`);
      return {
        success: false,
        message: `Campaign with name "${campaignName}" not found.`
      };
    }
    
    console.log(`Found campaign: ID=${campaign.id}, Name=${campaign.name}`);
    
    // Make the PATCH request to update the campaign status
    console.log(`Sending PATCH request to /campaigns/${campaign.id}/edit with isPublished=${isPublished}`);
    try {
      const response = await api.patch(`/campaigns/${campaign.id}/edit`, {
        isPublished
      });
      
      console.log('Campaign update response:', response.data);
      
      return {
        success: response.data?.success === false ? false : true,
        message: response.data?.message || `Campaign "${campaignName}" has been ${isPublished ? 'activated' : 'deactivated'} successfully.`,
        data: response.data
      };
    } catch (requestError) {
      console.error('Error making PATCH request:', requestError);
      console.error('Response data:', requestError.response?.data);
      return {
        success: false,
        message: `Error ${isPublished ? 'activating' : 'deactivating'} campaign: ${requestError.response?.data?.message || requestError.message || 'Unknown error'}`,
        error: requestError.response?.data || requestError.message
      };
    }
  } catch (error) {
    console.error('Error toggling campaign publish status:', error);
    return {
      success: false,
      message: `Error ${isPublished ? 'activating' : 'deactivating'} campaign: ${error.message || 'Unknown error'}`
    };
  }
};

/**
 * Deletes a campaign by name
 * @param {string} campaignName - The name of the campaign to delete
 * @returns {Promise<Object>} - The response from the server
 */
export const deleteCampaign = async (campaignName) => {
  try {
    // First, find the campaign by name using search
    console.log(`Searching for campaign with name: "${campaignName}" to delete`);
    
    // Use search parameter to filter campaigns by name (server-side search)
    const searchTerm = encodeURIComponent(campaignName);
    const campaignsResponse = await api.get(`/campaigns?search=${searchTerm}&limit=50`);
    const campaigns = campaignsResponse.data.campaigns || [];
    
    // Log all campaign names for debugging
    console.log('Search results:', campaigns.map(c => c.name));
    
    // Find the campaign with the matching name
    // Try exact match first (case-sensitive)
    let campaign = campaigns.find(c => c.name === campaignName);
    
    // If no exact match, try case-insensitive
    if (!campaign) {
      campaign = campaigns.find(c => 
        c.name.toLowerCase() === campaignName.toLowerCase()
      );
    }
    
    // If still not found, try partial match
    if (!campaign) {
      campaign = campaigns.find(c => 
        c.name.toLowerCase().includes(campaignName.toLowerCase()) ||
        campaignName.toLowerCase().includes(c.name.toLowerCase())
      );
    }
    
    if (!campaign) {
      console.error(`Campaign with name "${campaignName}" not found.`);
      return {
        success: false,
        message: `Campaign with name "${campaignName}" not found.`
      };
    }
    
    console.log(`Found campaign to delete: ID=${campaign.id}, Name=${campaign.name}`);
    
    // Make the DELETE request to delete the campaign
    console.log(`Sending DELETE request to /campaigns/${campaign.id}/delete`);
    try {
      const response = await api.delete(`/campaigns/${campaign.id}/delete`);
      
      console.log('Campaign delete response:', response.data);
      
      return {
        success: response.data?.success === false ? false : true,
        message: response.data?.message || `Campaign "${campaignName}" has been deleted successfully.`,
        data: response.data
      };
    } catch (requestError) {
      console.error('Error making DELETE request:', requestError);
      console.error('Response data:', requestError.response?.data);
      return {
        success: false,
        message: `Error deleting campaign: ${requestError.response?.data?.message || requestError.message || 'Unknown error'}`,
        error: requestError.response?.data || requestError.message
      };
    }
  } catch (error) {
    console.error('Error in deleteCampaign:', error);
    return {
      success: false,
      message: `Error deleting campaign: ${error.message || 'Unknown error'}`
    };
  }
};

/**
 * Clones a campaign by name
 * @param {string} campaignName - The name of the campaign to clone
 * @param {string} [newName] - Optional new name for the cloned campaign
 * @returns {Promise<Object>} - The response from the server
 */
export const cloneCampaign = async (campaignName, newName = null) => {
  try {
    // First, find the campaign by name using search
    console.log(`Searching for campaign with name: "${campaignName}" to clone`);
    
    // Use search parameter to filter campaigns by name (server-side search)
    const searchTerm = encodeURIComponent(campaignName);
    const campaignsResponse = await api.get(`/campaigns?search=${searchTerm}&limit=50`);
    const campaigns = campaignsResponse.data.campaigns || [];
    
    // Log all campaign names for debugging
    console.log('Search results:', campaigns.map(c => c.name));
    
    // Find the campaign with the matching name
    // Try exact match first (case-sensitive)
    let campaign = campaigns.find(c => c.name === campaignName);
    
    // If no exact match, try case-insensitive
    if (!campaign) {
      campaign = campaigns.find(c => 
        c.name.toLowerCase() === campaignName.toLowerCase()
      );
    }
    
    // If still not found, try partial match
    if (!campaign) {
      campaign = campaigns.find(c => 
        c.name.toLowerCase().includes(campaignName.toLowerCase()) ||
        campaignName.toLowerCase().includes(c.name.toLowerCase())
      );
    }
    
    if (!campaign) {
      console.error(`Campaign with name "${campaignName}" not found.`);
      return {
        success: false,
        message: `Campaign with name "${campaignName}" not found.`
      };
    }
    
    console.log(`Found campaign to clone: ID=${campaign.id}, Name=${campaign.name}`);
    
    // Make the POST request to clone the campaign
    console.log(`Sending POST request to /campaigns/clone/${campaign.id}`);
    try {
      const response = await api.post(`/campaigns/clone/${campaign.id}`);
      
      console.log('Campaign clone response:', response.data);
      
      // If we need to rename the cloned campaign
      if (newName && response.data.success && response.data.campaign && response.data.campaign.id) {
        console.log(`Cloning successful, now renaming to "${newName}"`);
        const clonedCampaignId = response.data.campaign.id;
        
        // Call the rename endpoint
        const renameResponse = await renameCampaign(clonedCampaignId, newName);
        
        if (renameResponse.success) {
          return {
            success: true,
            message: `Campaign "${campaignName}" cloned and renamed to "${newName}" successfully.`,
            data: response.data,
            newCampaignName: newName
          };
        } else {
          // Clone succeeded but rename failed
          return {
            success: true,
            message: `Campaign "${campaignName}" cloned successfully, but could not be renamed: ${renameResponse.message}`,
            data: response.data,
            newCampaignName: response.data?.campaign?.name || `${campaignName} (Clone)`
          };
        }
      }
      
      // Regular cloning without rename
      return {
        success: response.data?.success === false ? false : true,
        message: response.data?.message || `Campaign "${campaignName}" has been cloned successfully.`,
        data: response.data,
        newCampaignName: response.data?.campaign?.name || `${campaignName} (Clone)`
      };
    } catch (requestError) {
      console.error('Error making clone request:', requestError);
      console.error('Response data:', requestError.response?.data);
      return {
        success: false,
        message: `Error cloning campaign: ${requestError.response?.data?.message || requestError.message || 'Unknown error'}`,
        error: requestError.response?.data || requestError.message
      };
    }
  } catch (error) {
    console.error('Error in cloneCampaign:', error);
    return {
      success: false,
      message: `Error cloning campaign: ${error.message || 'Unknown error'}`
    };
  }
};

/**
 * Rename a campaign by ID
 * @param {string} campaignId - The ID of the campaign to rename
 * @param {string} newName - The new name for the campaign
 * @returns {Promise<Object>} - The response from the server
 */
export const renameCampaign = async (campaignId, newName) => {
  try {
    console.log(`Renaming campaign ID ${campaignId} to "${newName}"`);
    
    // Make the PATCH request to rename the campaign
    try {
      const response = await api.patch(`/campaigns/${campaignId}/rename`, {
        name: newName
      });
      
      console.log('Campaign rename response:', response.data);
      
      return {
        success: response.data?.success === false ? false : true,
        message: response.data?.message || `Campaign has been renamed to "${newName}" successfully.`,
        data: response.data,
        newName: newName
      };
    } catch (requestError) {
      console.error('Error making rename request:', requestError);
      console.error('Response data:', requestError.response?.data);
      return {
        success: false,
        message: `Error renaming campaign: ${requestError.response?.data?.message || requestError.message || 'Unknown error'}`,
        error: requestError.response?.data || requestError.message
      };
    }
  } catch (error) {
    console.error('Error in renameCampaign:', error);
    return {
      success: false,
      message: `Error renaming campaign: ${error.message || 'Unknown error'}`
    };
  }
};
